import React, { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom-v5-compat";
import { ROUTES } from "routing/routes";

import { credentialsRefreshHasErrored } from "seneca-common/features/user/state";
import { getReferralCodeFromLocation } from "seneca-common/utils/functions/referral";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";
import loaderWithRetry from "seneca-common/utils/loaderWithRetry";
import { analyticsReferredUserLandedOnSite } from "seneca-common/utils/senecaAnalytics";

import { linkToAnonymousCourseDiscoveryPage } from "features/anonymous-pages/course-discovery/links";
import { linkToAnonymousResourcesHubPage } from "features/anonymous-pages/resources-hub/links";
import { linkToAnonymousTutoringPage } from "features/anonymous-pages/tutoring-page/links";
import AnonymousTutoringRoute from "features/anonymous-pages/tutoring-page/Route";
import { linkToAnonymousUnsubscribePage } from "features/anonymous-pages/unsubscribe/links";
import AnonymousUnsubscribePageRoute from "features/anonymous-pages/unsubscribe/Route";
import { linkToAnonymousWhatIsPremium } from "features/anonymous-pages/what-is-premium/links";
import AnonymousWhatIsPremium from "features/anonymous-pages/what-is-premium/Route";
import lazyWithPreload from "features/app/components/lazyWithPreload";
import SelfDiagnosisPage from "features/app/diagnosis/DiagnosisPage";
import UpdateAppPage from "features/app/update/UpdateAppPage";
import LoadableClassroom from "features/classroom/routes";
import { linkToClassroom } from "features/classroom/routes/links";
import { PageNotFoundError } from "features/common/components/AppErrors";
import CredentialsExpiredModal from "features/common/components/CredentialsExpiredModal";
import { SplashScreenForLoadableChunk } from "features/common/loadingScreens/components/SplashScreen";
import {
  browserVersionIsNotSupported,
  BrowserVersionNotSupportedModal
} from "features/common/modals/components/DeviceWarningModal";
import RedirectToAppropriateHomepage from "features/common/routing/RedirectToAppropriateHomePage";
import SignedInRoute from "features/common/routing/SignedInRoute";
import { linkToCreationHome } from "features/course-creation/links";
// This unused import improves the code split NavBar load time
import "features/dashboard/features/dashboard-navbar";
import { linkToDashboard } from "features/dashboard/links";
import useRedirectToSelectAccountType from "features/dashboard/pages/hooks/useRedirectToSelectAccountType";
import DashboardRoute from "features/dashboard/routing/DashboardRoute";
import { linkToParentDashboard } from "features/parents/links";
import LoadableParentPlatform from "features/parents/routes";
import { useIsPlurallApp } from "features/plurall";
import { linkToPageNotFoundError } from "features/plurall/links";
import { linkToMobileAuthRoute } from "features/pwa/mobile/auth/links";
import { isNativeAppiOS } from "features/pwa/mobile/ios/utils/isNativeAppiOS";
import CompleteAccountTypeModal from "features/sign-up/complete-account/CompleteAccountTypeModal/CompleteAccountTypeModal";
import { linkToCompleteAccountType } from "features/sign-up/complete-account/links";
import {
  linkToPasswordReset,
  linkToSignIn,
  linkToSignUp,
  linkToSignUpInvite,
  linkToSignUpParent
} from "features/sign-up/links";
import { linkToGetPremium } from "features/subscriptions/routes/get-premium/links";
import "features/teachers/features/teacher-navbar";
import { linkToTeacherDashboard } from "features/teachers/links";
import LoadableTeacherPlatform from "features/teachers/routes";
import { TutoringActivationJourneyRoutes } from "features/tutoring/activation-journey";
import { getShowPlurallHelpCentreModalOpen } from "features/ui-state/selectors";

import {
  linkToResourceDiscover,
  linkToTutorLandingPage_deprecated
} from "./features/resource-discover/routes/links";
import { ModalRoutes } from "./ModalRoutes";

const LoadableCreation = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "creation-chunk" */
        "features/course-creation/pages/course-creation-page/components/CourseCreation"
      )
  )
);
const SignUpParentRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-up-invite-chunk" */ "features/sign-up/common-routes/components/SignUpParentRoute"
      )
  )
);
const LoadableDashboardRouter = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "dashboard-router-chunk" */ "features/dashboard/containers/DashboardRouter"
      )
  )
);

const LoadableFrontDoor = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "front-door-chunk" */ "features/sign-up/components/FrontDoor"
      )
  )
);
const SignUpInviteRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-up-invite-chunk" */
        "features/sign-up/common-routes/components/SignUpInviteRoute"
      )
  )
);

export const PreloadableGetPremiumRoute = lazyWithPreload(
  () =>
    import(
      /* webpackChunkName: "get-premium-chunk" */ "features/subscriptions/routes/get-premium/Route"
    )
);

const LoadableAnonymousCourseDiscovery = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-course-discovery-chunk" */
        "features/anonymous-pages/course-discovery/Route"
      )
  )
);

const LoadableAnonymousResourcesHubDiscovery = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-resources-hub-chunk" */
        "features/anonymous-pages/resources-hub/Route"
      )
  )
);

const LoadablePlurallHelpCentreModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "plurall-help-centre-modal-chunk" */
        "features/plurall/components/PlurallHelpCentreModal/PlurallHelpCentreModal"
      )
  )
);

const LoadableResourceDiscover = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-resources-hub" */
        "features/resource-discover/routes/Route"
      )
  )
);

const MobileAuthRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mobile-auth" */
        "features/pwa/mobile/auth/screens/Route"
      )
  )
);

export default function Routes() {
  const location = useLocation();
  const history = useHistory();
  const refreshCredentialsError = useSelector(credentialsRefreshHasErrored);
  const showPlurallHelpCentreModal = useSelector(
    getShowPlurallHelpCentreModalOpen
  );
  const isPlurallApp = useIsPlurallApp();

  useEffectOnMount(() => {
    logReferralCode(location);

    if (window.Cypress) {
      window.__routerHistory__ = history;
    }
  });

  useRedirectToSelectAccountType();

  if (browserVersionIsNotSupported()) {
    return <BrowserVersionNotSupportedModal />;
  }

  return (
    <>
      <Suspense fallback={<SplashScreenForLoadableChunk />}>
        <Route path={"/"}>
          <TutoringActivationJourneyRoutes />
        </Route>
        <Switch location={location.state?.background || location}>
          <Route path={ROUTES.UPDATE.path}>
            <UpdateAppPage />
          </Route>
          <Route path={ROUTES.DIAGNOSIS.path}>
            <SelfDiagnosisPage />
          </Route>
          <Route path={linkToSignUp()} component={LoadableFrontDoor} />
          <Route path={linkToSignIn()} component={LoadableFrontDoor} />
          <Route path={linkToPasswordReset()} component={LoadableFrontDoor} />
          <Route path={linkToCompleteAccountType()}>
            <CompleteAccountTypeModal />
          </Route>
          <Route path={linkToSignUpInvite()}>
            <SignUpInviteRoute />
          </Route>
          <Route path={linkToSignUpParent()}>
            <SignUpParentRoute />
          </Route>
          {isPlurallApp ? (
            <Route path={linkToClassroom()}>
              <SignedInRoute>
                <LoadableClassroom />
              </SignedInRoute>
            </Route>
          ) : (
            <Route path={linkToClassroom()}>
              <LoadableClassroom />
            </Route>
          )}
          <Route path={linkToCreationHome()}>
            <SignedInRoute>
              <LoadableCreation />
            </SignedInRoute>
          </Route>

          <Route path={linkToGetPremium()}>
            <PreloadableGetPremiumRoute />
          </Route>

          <Route path={linkToDashboard()}>
            <DashboardRoute>
              <LoadableDashboardRouter />
            </DashboardRoute>
          </Route>

          <Route path={linkToTeacherDashboard()}>
            <SignedInRoute>
              <LoadableTeacherPlatform />
            </SignedInRoute>
          </Route>

          <Route path={linkToParentDashboard()}>
            <SignedInRoute>
              <LoadableParentPlatform />
            </SignedInRoute>
          </Route>

          <Route path={linkToAnonymousWhatIsPremium()}>
            <AnonymousWhatIsPremium />
          </Route>
          {!isPlurallApp && (
            <Route path={linkToAnonymousCourseDiscoveryPage()}>
              <LoadableAnonymousCourseDiscovery />
            </Route>
          )}
          {!isPlurallApp && (
            <Route path={linkToAnonymousResourcesHubPage()}>
              <LoadableAnonymousResourcesHubDiscovery />
            </Route>
          )}
          <Route path={linkToAnonymousUnsubscribePage()}>
            <AnonymousUnsubscribePageRoute />
          </Route>

          {!isPlurallApp && (
            <Route path={linkToResourceDiscover()}>
              <LoadableResourceDiscover />
            </Route>
          )}
          {!isPlurallApp && (
            <Route path={linkToTutorLandingPage_deprecated()}>
              <Redirect
                to={{ ...location, pathname: linkToResourceDiscover() }}
              />
            </Route>
          )}

          {/* TODO added temporary default error page in case seneca links were missed. Remove once all links are replaced for SOMOS app */}
          {isPlurallApp && (
            <Route path={linkToPageNotFoundError()}>
              <PageNotFoundError />
            </Route>
          )}

          {isNativeAppiOS() && (
            <Route path={linkToMobileAuthRoute()}>
              <MobileAuthRoute />
            </Route>
          )}

          {!isPlurallApp && (
            <Route path={linkToAnonymousTutoringPage()}>
              <AnonymousTutoringRoute />
            </Route>
          )}
          <RedirectToAppropriateHomepage />
        </Switch>
      </Suspense>
      {refreshCredentialsError && <CredentialsExpiredModal />}
      {isPlurallApp && showPlurallHelpCentreModal && (
        <LoadablePlurallHelpCentreModal />
      )}
      <ModalRoutes />
    </>
  );
}

function logReferralCode(location: any) {
  const referral = location && getReferralCodeFromLocation(location);

  referral && analyticsReferredUserLandedOnSite(referral, location.pathname);
}
